/// <reference path="Tag.ts" />
/// <reference path="../Util.ts" />

namespace RS
{
    export interface CallbackMethod extends Function
    {
        owner: object;
    }

    interface CtorWithCallbacks extends Function
    {
        _hasCallbacks?: boolean;
        _callbacks?: (string | symbol)[];
    }

    /**
     * Marks the specified class as as having callbacks.
     * Note that this only works for non-static members of classes.
     */
    export const HasCallbacks = Decorators.Tag.createDataless(Decorators.TagKind.Class, (oldCtor) =>
    {
        const newCtor = Util.renameFunction(function()
        {
            bindCallbacks(this, Decorators.MemberType.Instance);
            oldCtor.apply(this, arguments);
        }, oldCtor["name"] || "Object");
        newCtor.prototype = oldCtor.prototype;
        const oldProps = Object.getOwnPropertyNames(oldCtor);
        for (const propName of oldProps)
        {
            if (!(propName in newCtor))
            {
                const descriptor = Object.getOwnPropertyDescriptor(oldCtor, propName);
                if (descriptor)
                {
                    Object.defineProperty(newCtor, propName, descriptor);
                }
                else
                {
                    newCtor[propName] = oldCtor[propName];
                }
            }
        }
        return newCtor;
    });

    /**
     * Marks the specified method as a callback, allowing it to be passed directly into Event.on.
     * The owner class must be marked with HasCallbacks, if this is applied to an instance member.
     */
    export const Callback = Decorators.Tag.createDataless(Decorators.TagKind.Method);

    /**
     * Binds all @Callback marked methods to this on the specified object.
     * Should not be required to call this manually unless working with static classes.
     */
    export function bindCallbacks(target: object, type: Decorators.MemberType)
    {
        const callbacks = Callback.get(target, type);
        if (callbacks != null)
        {
            for (const methodName of callbacks)
            {
                target[methodName] = target[methodName].bind(target);
                // Log.debug(`@Callback: bound ${methodName} for ${target}`);
            }
        }
    }
}